import { useState, useEffect, ReactNode } from 'react'
import { ThemeModeProvider, useThemeModeState, Theme } from '@magnetic/theme'
import { ThemeContext, type ThemeValue } from '@/lib/theme-context-definition'

export type { ThemeValue, ThemeContextType } from '@/lib/theme-context-definition'

export function ThemeProvider({ children }: { children: ReactNode }) {
  // Load theme from localStorage, default to 'system' if not set
  const [themeValue, setThemeValue] = useState<ThemeValue>(() => {
    const stored = localStorage.getItem('theme-preference')
    return (stored as ThemeValue) || 'system'
  })
  const themeModeState = useThemeModeState({ value: themeValue })

  // Persist theme changes to localStorage
  useEffect(() => {
    localStorage.setItem('theme-preference', themeValue)
  }, [themeValue])

  // Sync Theme.instance.current for portal components (Header dropdown, etc.)
  useEffect(() => {
    const themeSelector = themeModeState.mode === 'dark'
      ? 'blue-dark'
      : 'blue-light'
    Theme.instance.current = themeSelector
  }, [themeModeState.mode])

  return (
    <ThemeContext.Provider
      value={{
        themeValue,
        setThemeValue,
        currentMode: themeModeState.mode,
      }}
    >
      <ThemeModeProvider
        state={themeModeState}
        attribute="data-mds-theme"
        light="blue-light-classic"
        dark="blue-dark-classic"
      >
        {children}
      </ThemeModeProvider>
    </ThemeContext.Provider>
  )
}
